import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Heart, Wrench, HandshakeIcon, Handshake } from "lucide-react";

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="pt-40 pb-16">
        <div className="wrap grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow eyebrow-red">Meet the shop · Prescott Valley, AZ</div>
            <h1 className="h-display text-6xl md:text-8xl uppercase mt-4">
              I&apos;m Dave. <br/>
              <span className="italic font-normal text-neutral-500">Welcome to 928.</span>
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-lg text-neutral-700 leading-relaxed">
              I started 928 Wraps because I got tired of watching great local businesses stay invisible. You do amazing work — but if nobody sees you rolling around town, they don&apos;t know you exist. That&apos;s the entire problem this shop solves.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="wrap grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative">
            <img
              src="https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/dmpaw94x_Webpage_MainImage.webp"
              alt="Dave Mitchell — owner of 928 Wraps in Prescott Valley, Arizona"
              className="rounded-3xl w-full aspect-[3/4] object-cover"
              loading="eager"
            />
          </div>
          <div className="md:col-span-6">
            <div className="eyebrow">The 928 way</div>
            <h2 className="h-display text-4xl md:text-5xl uppercase mt-3">Relationships <span className="italic font-normal">over</span> transactions.</h2>
            <p className="mt-6 text-neutral-700 leading-relaxed">
              You&apos;ll never feel like just another job on the schedule. Every project I take on, I take on personally — from the first phone call to the final walk-around before you drive off the lot. If something isn&apos;t right, we make it right. That&apos;s the whole company.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flat-card rounded-2xl p-6">
                <Heart size={22} className="text-[var(--red)]" />
                <div className="font-display text-xl mt-4">Personal attention</div>
                <div className="text-sm text-neutral-600 mt-1">Owner on every project. Every time.</div>
              </div>
              <div className="flat-card rounded-2xl p-6">
                <Wrench size={22} className="text-[var(--red)]" />
                <div className="font-display text-xl mt-4">Premium materials</div>
                <div className="text-sm text-neutral-600 mt-1">3M &amp; Avery cast films. Nothing less.</div>
              </div>
              <div className="flat-card rounded-2xl p-6">
                <Handshake size={22} className="text-[var(--red)]" />
                <div className="font-display text-xl mt-4">We stand behind it</div>
                <div className="text-sm text-neutral-600 mt-1">If it&apos;s wrong, we make it right.</div>
              </div>
              <div className="flat-card rounded-2xl p-6">
                <MapPin size={22} className="text-[var(--red)]" />
                <div className="font-display text-xl mt-4">Local. Really local.</div>
                <div className="text-sm text-neutral-600 mt-1">Prescott Valley &amp; all of Northern AZ.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inside the shop */}
      <section className="pb-24">
        <div className="wrap">
          <div className="max-w-3xl mb-10">
            <div className="eyebrow">Inside the shop</div>
            <h2 className="h-display text-4xl md:text-6xl uppercase mt-3">
              8920 E Valley Rd. <span className="italic font-normal text-neutral-500">Come by.</span>
            </h2>
            <p className="mt-6 text-lg text-neutral-700 leading-relaxed">
              This is where every project starts, gets designed, and comes together. Real work, real hands, real space — no black-box back rooms. Stop by anytime during business hours.
            </p>
          </div>
          <div className="relative overflow-hidden rounded-3xl">
            <img
              src="https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/htfzwobs_IMG_0586.webp"
              alt="Inside the 928 Wraps shop in Prescott Valley — vehicles being wrapped, 928 branded wall mural"
              className="w-full aspect-[4/3] md:aspect-[16/9] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="wrap">
          <div className="bg-[var(--bg-2)] border border-neutral-200 rounded-3xl p-10 md:p-16">
            <div className="eyebrow eyebrow-red">The mission</div>
            <h3 className="h-display text-4xl md:text-6xl uppercase mt-3 max-w-4xl">
              Helping Northern Arizona <br/> businesses <span className="italic font-normal text-neutral-500">get seen.</span>
            </h3>
            <p className="mt-6 text-neutral-700 leading-relaxed max-w-3xl">
              We don&apos;t compete on being the cheapest. We compete on being the shop you&apos;re proud to recommend. Wraps, tint, signs — they&apos;re just the tools. The real product is a business that finally looks as good as the work it does.
            </p>
            <div className="mt-8">
              <Link to="/mockup" data-testid="about-cta" className="btn-red">Start with a free mockup <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
